import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Plus, Trophy, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MatchCard } from "@/components/coupon/MatchCard";
import { EmptyState } from "@/components/common/EmptyState";
import { InlineLoading } from "@/components/common/Loading";
import { useMatches } from "@/hooks/useMatches";
import { useUi } from "@/stores/ui";
import { useCouponCart } from "@/stores/couponCart";
import { format } from "date-fns";
import { formatMatchDay } from "@/lib/format";
import { cn } from "@/lib/utils";

type TabKey = "today" | "live" | "week" | "month";

const POPULAR_LEAGUES: Array<{ id: number; name: string }> = [
  { id: 0, name: "Tous" },
  { id: 1, name: "Mondial" },
  { id: 2, name: "Champions" },
  { id: 4, name: "Euro" },
  { id: 39, name: "Premier League" },
  { id: 140, name: "La Liga" },
  { id: 61, name: "Ligue 1" },
  { id: 78, name: "Bundesliga" },
  { id: 135, name: "Serie A" },
  { id: 12, name: "CAF CL" },
];

/**
 * Saison à passer à api-football pour les compétitions à édition unique.
 * Sans `season`, l'endpoint `/fixtures?league=X` peut renvoyer une liste
 * vide silencieusement (cas confirmé sur la World Cup).
 *
 * Format : année civile de l'édition (Mondial 2026 = 2026, Euro 2024 = 2024).
 * Les championnats (39 Premier League, 140 La Liga, etc.) suivent une logique
 * de saison académique (août→mai) ; on les laisse hors map et l'API choisit
 * par défaut la saison en cours.
 */
const LEAGUE_CURRENT_SEASON: Record<number, number> = {
  1: 2026, // FIFA World Cup 2026 (USA / Mexico / Canada — juin-juillet 2026)
};

/**
 * Détermine les paramètres à envoyer à api-football-fixtures selon l'onglet,
 * la ligue sélectionnée et la présence éventuelle d'une saison fixée.
 *
 * Trois cas :
 *
 *   a) Live → `live=all`
 *   b) Ligue dans LEAGUE_CURRENT_SEASON (Mondial, Euro à édition unique) →
 *      ne rien envoyer comme paramètre temporel ; le caller passera juste
 *      `league + season` → l'API renvoie le schedule complet de la saison,
 *      filtré ensuite côté client par l'horizon du tab. Évite l'erreur
 *      api-football "next + season cannot be combined".
 *   c) Sinon → `date` ou `next=N` selon le tab.
 */
function getFetchParams(
  tab: TabKey,
  hasLeague: boolean,
  hasFixedSeason: boolean,
  today: string,
): { live?: boolean; date?: string; next?: number } {
  if (tab === "live") return { live: true };
  if (hasFixedSeason) return {}; // schedule complet via league+season
  if (tab === "today" && !hasLeague) return { date: today };
  if (tab === "today" && hasLeague) return { next: 100 };
  if (tab === "week") return { next: 200 };
  if (tab === "month") return { next: 400 };
  return {};
}

/**
 * Horizon (en jours) appliqué côté client après fetch, quand on est en mode
 * `next=N`. Retourne null quand l'API a déjà filtré par date (today sans
 * ligue) ou quand il n'y a pas d'horizon temporel (live).
 */
function getHorizonDays(tab: TabKey, hasLeague: boolean): number | null {
  if (tab === "live") return null;
  if (tab === "today" && !hasLeague) return null;
  if (tab === "today" && hasLeague) return 1;
  if (tab === "week") return 30;
  if (tab === "month") return 60;
  return null;
}

export function Matches() {
  const { i18n } = useTranslation();
  const [tab, setTab] = useState<TabKey>("today");
  const [leagueId, setLeagueId] = useState<number>(0);
  const [search, setSearch] = useState("");
  const { setCouponSheetOpen } = useUi();
  const cartType = useCouponCart((s) => s.type);

  const today = format(new Date(), "yyyy-MM-dd");
  const hasLeague = leagueId > 0;
  const season = LEAGUE_CURRENT_SEASON[leagueId];
  const fetchParams = getFetchParams(tab, hasLeague, !!season, today);
  const matchesQ = useMatches({
    ...fetchParams,
    league: leagueId || undefined,
    season,
    search: search.trim() || undefined,
  });

  const filtered = useMemo(() => {
    const all = matchesQ.data ?? [];
    const q = search.trim().toLowerCase();
    // Pour les ligues à saison fixe (Mondial, etc.) on retire le bornage
    // d'horizon → on affiche tous les matchs à venir du calendrier complet,
    // pas seulement ceux du jour / de la semaine / du mois.
    const horizonDays = season ? null : getHorizonDays(tab, hasLeague);
    const horizonMs = horizonDays != null ? horizonDays * 24 * 60 * 60 * 1000 : Infinity;
    const now = Date.now();
    const bounded = horizonMs === Infinity
      ? all
      : all.filter((m) => {
          const t = new Date(m.kickoff).getTime();
          return t >= now - 2 * 60 * 60 * 1000 && t <= now + horizonMs;
        });
    // Hors onglet Live : on ne montre que les matchs "ouverts" (pas encore
    // commencés), c.-à-d. status NS. Les déjà joués/en cours sont masqués.
    const openOnly = tab === "live"
      ? bounded
      : bounded.filter((m) => m.status === "NS");
    return q
      ? openOnly.filter(
          (m) =>
            m.home.name.toLowerCase().includes(q) || m.away.name.toLowerCase().includes(q)
        )
      : openOnly;
  }, [matchesQ.data, search, tab, hasLeague, season]);

  const grouped = useMemo(() => {
    const map = new Map<string, typeof filtered>();
    for (const m of filtered) {
      const key = format(new Date(m.kickoff), "yyyy-MM-dd");
      const arr = map.get(key) ?? [];
      arr.push(m);
      map.set(key, arr);
    }
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="space-y-4 pt-2 pb-2">
      <header className="space-y-3">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">Matchs</h1>
          <Button
            size="sm"
            onClick={() => setCouponSheetOpen(true)}
            disabled={!!cartType}
          >
            <Plus className="size-3.5" />
            {cartType ?? "Coupon"}
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher une équipe…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 pr-10 h-10 bg-white/[0.03] border-white/[0.06] rounded-lg"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
              aria-label="clear"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>

        {/* Tabs — segmented control */}
        <div className="grid grid-cols-4 gap-0.5 p-0.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
          {([
            { id: "today", label: "Aujourd'hui" },
            { id: "live", label: "Live", live: true },
            { id: "week", label: "Semaine" },
            { id: "month", label: "Mois" },
          ] as Array<{ id: TabKey; label: string; live?: boolean }>).map((s) => (
            <button
              key={s.id}
              onClick={() => setTab(s.id)}
              className={cn(
                "h-8 text-xs font-medium transition-all duration-180 rounded-md flex items-center justify-center gap-1.5",
                tab === s.id
                  ? "bg-white/[0.08] text-foreground shadow-soft"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {s.live && tab === s.id && <span className="size-1.5 rounded-full bg-destructive animate-pulse" />}
              {s.label}
            </button>
          ))}
        </div>

        {/* Leagues — chips horizontaux */}
        <div className="flex gap-1.5 overflow-x-auto scrollbar-hidden -mx-4 px-4">
          {POPULAR_LEAGUES.map((l) => (
            <button
              key={l.id}
              onClick={() => setLeagueId(l.id)}
              className={cn(
                "shrink-0 inline-flex items-center h-8 px-3 text-2xs font-medium rounded-md transition-all duration-180",
                leagueId === l.id
                  ? "bg-primary/15 text-primary border border-primary/30"
                  : "bg-white/[0.03] text-muted-foreground border border-white/[0.06] hover:text-foreground hover:bg-white/[0.06]"
              )}
            >
              {l.name}
            </button>
          ))}
        </div>
      </header>

      {matchesQ.isLoading ? (
        <InlineLoading label="Chargement…" />
      ) : grouped.length === 0 ? (
        <EmptyState icon={Trophy} title="Aucun match" description="Essaye un autre filtre ou une autre ligue." />
      ) : (
        <div className="space-y-5">
          {grouped.map(([date, matches]) => (
            <section key={date} className="space-y-2">
              <div className="flex items-center justify-between px-0.5">
                <h2 className="text-sm font-semibold tracking-tight">
                  {formatMatchDay(date, i18n.language as "fr" | "en")}
                </h2>
                <span className="text-2xs text-muted-foreground tabular-nums">
                  {matches.length} {matches.length > 1 ? "matchs" : "match"}
                </span>
              </div>
              <div className="space-y-2">
                {matches.map((m) => (
                  <MatchCard key={m.id} match={m} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
